import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import heroImage from '../../assets/images_of_victoria/IMG_2296.jpg';
import supportImage from '../../assets/images_of_victoria/IMG_2250.jpg';
import { useSiteAuth } from '../hooks/useSiteAuth';
import {
  createDonationIntent,
  createDonationSubscription,
  getSitePaymentConfig,
} from '../services/event-payments';
import {
  calculateCoveredFeeCents,
  calculateCoveredTotalCents,
  calculateEstimatedProcessingFeeCents,
} from '../services/payment-fees';
import { getPreviewBase } from '../services/bliss-app-auth';
import type { DonationAmount, DonationFrequency } from '../types';

const donationAmounts: DonationAmount[] = [
  { amount: 25, label: '$25' },
  { amount: 50, label: '$50' },
  { amount: 100, label: '$100' },
  { amount: 250, label: '$250' },
  { amount: 500, label: '$500' },
  { amount: 1000, label: '$1,000' },
];

const supportPoints = [
  {
    title: 'Program continuity',
    description: 'Giving helps keep creative, educational, and movement-based programming present and available.',
  },
  {
    title: 'Family encouragement',
    description: 'Support helps create a place where families feel welcomed, understood, and connected to community.',
  },
  {
    title: 'Future growth',
    description: "Every gift strengthens the long-term stability of Victoria's Place and the work still ahead.",
  },
];

const stewardshipNotes = [
  'Support is directed toward children, families, and the environment needed to serve them with consistency.',
  "Victoria's Place exists because one mother believed children with Down Syndrome deserve places created with care.",
  'Giving is more than a transaction here. It is participation in a mission built on faith, family, and community.',
];

function parseDonationAmount(value: string): number | null {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 1) {
    return null;
  }

  return Math.round(amount * 100) / 100;
}

function calculateApplicationFeeCents(amountCents: number): number {
  if (!Number.isFinite(amountCents) || amountCents <= 0) {
    return 0;
  }

  return Math.max(0, Math.round(amountCents * 0.022));
}

function formatUsdCents(amountCents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountCents / 100);
}

function buildGiveReturnUrl(params: URLSearchParams): string {
  const previewBase = getPreviewBase();
  const path = `${previewBase || ''}/give/thank-you?${params.toString()}`;
  return new URL(path, window.location.origin).toString();
}

interface DonationPaymentFormProps {
  chargedAmountCents: number;
  clientSecret: string;
  donorEmail: string;
  donorName: string;
  frequency: DonationFrequency;
  submitLabel: string;
  onComplete: (input: {
    amountCents: number;
    donorEmail: string;
    paymentIntentId: string | null;
    status: 'processing' | 'succeeded';
  }) => void;
}

function DonationPaymentForm({
  chargedAmountCents,
  clientSecret,
  donorEmail,
  donorName,
  frequency,
  submitLabel,
  onComplete,
}: DonationPaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function applyRecoveredStatus(status: string, paymentIntentId: string | null = null) {
    if (status === 'succeeded' || status === 'processing') {
      onComplete({
        amountCents: chargedAmountCents,
        donorEmail,
        paymentIntentId,
        status: status === 'processing' ? 'processing' : 'succeeded',
      });
      return true;
    }

    return false;
  }

  async function resolvePaymentStateFallback() {
    if (!stripe) {
      return false;
    }

    const retrieved = await stripe.retrievePaymentIntent(clientSecret);
    return applyRecoveredStatus(retrieved.paymentIntent?.status || '', retrieved.paymentIntent?.id ?? null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    const returnParams = new URLSearchParams();
    returnParams.set('type', frequency === 'monthly' ? 'monthly_donation' : 'donation');
    returnParams.set('amount_cents', String(chargedAmountCents));
    returnParams.set('email', donorEmail);

    setPaymentError(null);
    setIsSubmitting(true);

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: buildGiveReturnUrl(returnParams),
          payment_method_data: {
            billing_details: {
              ...(donorName ? { name: donorName } : {}),
              ...(donorEmail ? { email: donorEmail } : {}),
            },
          },
        },
        redirect: 'if_required',
      });

      if (result.error) {
        const recovered = await resolvePaymentStateFallback();
        if (recovered) {
          return;
        }

        setPaymentError(result.error.message || 'Payment failed. Please try again.');
        return;
      }

      const status = result.paymentIntent?.status;
      if (applyRecoveredStatus(status || '', result.paymentIntent?.id ?? null)) {
        return;
      }

      const recovered = await resolvePaymentStateFallback();
      if (recovered) {
        return;
      }

      setPaymentError('Payment was not completed. Please try again.');
    } catch (error) {
      try {
        const recovered = await resolvePaymentStateFallback();
        if (recovered) {
          return;
        }
      } catch {
        // Keep the original payment error when Stripe fallback lookup also fails.
      }

      setPaymentError(error instanceof Error ? error.message : 'Payment failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="give-payment-form" onSubmit={handleSubmit}>
      <PaymentElement />
      {paymentError ? <div className="auth-error">{paymentError}</div> : null}
      <button type="submit" className="button-primary tap-target give-submit-button" disabled={!stripe || !elements || isSubmitting}>
        {isSubmitting ? 'Processing secure payment...' : submitLabel}
      </button>
    </form>
  );
}

export default function Give() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, isInitializing } = useSiteAuth();
  const [frequency, setFrequency] = useState<DonationFrequency>('one-time');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(100);
  const [customAmount, setCustomAmount] = useState('');
  const [coverProcessingFee, setCoverProcessingFee] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isPreparingCheckout, setIsPreparingCheckout] = useState(false);
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [elementsOptions, setElementsOptions] = useState<StripeElementsOptions | null>(null);

  const selectedAmountValue = customAmount || (selectedAmount ? String(selectedAmount) : '');
  const parsedAmount = useMemo(() => parseDonationAmount(selectedAmountValue), [selectedAmountValue]);
  const baseAmountCents = parsedAmount ? Math.round(parsedAmount * 100) : 0;
  const coveredFeeCents = coverProcessingFee ? calculateCoveredFeeCents(baseAmountCents) : 0;
  const chargedAmountCents = coverProcessingFee ? calculateCoveredTotalCents(baseAmountCents) : baseAmountCents;
  const estimatedProcessingFeeCents = calculateEstimatedProcessingFeeCents(chargedAmountCents);
  const applicationFeeCents = calculateApplicationFeeCents(chargedAmountCents);
  const monthlyNeedsAuth = frequency === 'monthly' && !isInitializing && !session;
  const status = searchParams.get('status');
  const isMonthlyReturn = searchParams.get('type') === 'monthly_donation';
  const returnedAmountCents = Number(searchParams.get('amount_cents') || '0');
  const returnedEmail = searchParams.get('email') || donorEmail;

  useEffect(() => {
    if (!session) {
      return;
    }

    if (!donorName.trim()) {
      setDonorName(session.name);
    }

    if (!donorEmail.trim()) {
      setDonorEmail(session.email);
    }
  }, [session, donorEmail, donorName]);

  function resetCheckoutState() {
    setCheckoutError(null);
    setStripePromise(null);
    setElementsOptions(null);
  }

  function updateFrequency(nextFrequency: DonationFrequency) {
    setFrequency(nextFrequency);
    resetCheckoutState();
  }

  function handlePaymentComplete(input: {
    amountCents: number;
    donorEmail: string;
    paymentIntentId: string | null;
    status: 'processing' | 'succeeded';
  }) {
    const nextParams = new URLSearchParams();
    nextParams.set('type', frequency === 'monthly' ? 'monthly_donation' : 'donation');
    nextParams.set('status', input.status);
    nextParams.set('amount_cents', String(input.amountCents));
    nextParams.set('email', input.donorEmail);
    if (input.paymentIntentId) {
      nextParams.set('payment_intent', input.paymentIntentId);
    }

    navigate({ pathname: '/give/thank-you', search: nextParams.toString() });
  }

  async function handlePrepareCheckout() {
    if (!parsedAmount) {
      setCheckoutError('Please enter a valid donation amount of at least $1.');
      return;
    }

    if (!donorEmail.trim()) {
      setCheckoutError("Please enter an email so Victoria's Place can send your donation receipt.");
      return;
    }

    if (frequency === 'monthly' && !session) {
      setCheckoutError('Sign in or create a donor account to start a monthly gift.');
      return;
    }

    setCheckoutError(null);
    setIsPreparingCheckout(true);

    try {
      const paymentConfig = await getSitePaymentConfig();
      if (!paymentConfig.publishable_key) {
        throw new Error('Stripe publishable key is not configured for this project.');
      }
      if (!paymentConfig.stripe_connect_account_configured) {
        throw new Error('Stripe connected account is not configured for this project.');
      }

      if (frequency === 'monthly') {
        const subscription = await createDonationSubscription({
          amount: chargedAmountCents / 100,
          donorName: donorName.trim(),
          donorEmail: donorEmail.trim(),
          appUserEmail: session?.email,
          applicationFeePercent: 2.2,
          metadata: {
            source: 'give_page',
            donation_type: 'monthly',
            donor_base_amount_cents: String(baseAmountCents),
            donor_cover_processing_fee: coverProcessingFee ? 'true' : 'false',
            donor_cover_fee_cents: String(coveredFeeCents),
          },
        });

        if (!subscription.client_secret) {
          throw new Error('Could not initialize monthly donation checkout.');
        }

        setStripePromise(
          loadStripe(
            paymentConfig.publishable_key,
            subscription.stripe_account_id ? { stripeAccount: subscription.stripe_account_id } : undefined,
          ),
        );
        setElementsOptions({
          clientSecret: subscription.client_secret,
          appearance: { theme: 'stripe' },
        });
        return;
      }

      const intent = await createDonationIntent({
        amount: chargedAmountCents / 100,
        donorName: donorName.trim(),
        donorEmail: donorEmail.trim(),
        applicationFeeAmount: applicationFeeCents,
        metadata: {
          source: 'give_page',
          donation_type: 'one_time',
          donor_base_amount_cents: String(baseAmountCents),
          donor_cover_processing_fee: coverProcessingFee ? 'true' : 'false',
          donor_cover_fee_cents: String(coveredFeeCents),
          application_fee_bps: '220',
        },
      });

      if (!intent.client_secret) {
        throw new Error('Could not initialize donation checkout.');
      }

      setStripePromise(
        loadStripe(
          paymentConfig.publishable_key,
          intent.stripe_account_id ? { stripeAccount: intent.stripe_account_id } : undefined,
        ),
      );
      setElementsOptions({
        clientSecret: intent.client_secret,
        appearance: { theme: 'stripe' },
      });
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : 'Could not start donation checkout right now.');
    } finally {
      setIsPreparingCheckout(false);
    }
  }

  return (
    <div className="give-page">
      <section className="give-hero give-bleed">
        <div className="give-hero-media">
          <img src={heroImage} alt="Victoria's Place giving and community support" />
        </div>

        <motion.div
          className="give-hero-inner"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <div className="give-hero-copy">
            <span className="eyebrow give-eyebrow">Give</span>
            <p className="give-kicker">Support the mission with generosity and trust</p>
            <h1>Help keep Victoria&apos;s Place welcoming, steady, and available to families.</h1>
            <p className="give-hero-lead">
              Every gift helps support programs, encouragement, and a safe environment where children with Down Syndrome
              can create, grow, and be seen.
            </p>
          </div>
        </motion.div>
      </section>

      <div className="page-shell give-shell">
        <motion.section
          className="give-intro-grid"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <div className="give-story">
            <p className="home-section-label">Why It Matters</p>
            <h2>Giving helps protect the kind of space every child deserves.</h2>
            <p>
              Victoria&apos;s Place was created from lived experience, family love, and the belief that children with Down
              Syndrome deserve more than generic support. They deserve spaces built with patience, joy, and real care.
            </p>
            <p>
              Donations help keep that mission present through programs, family encouragement, and the steady work of
              creating a place where children are welcomed and celebrated.
            </p>
          </div>

          <div className="give-form-panel">
            <div className="give-form-heading">
              <p className="info-kicker">Make a gift</p>
              <h3>Choose a way to support Victoria&apos;s Place.</h3>
            </div>

            {status === 'succeeded' ? (
              <div className="give-status-card give-status-card--success">
                <p className="info-kicker">Donation received</p>
                <h4>{isMonthlyReturn ? 'Your monthly gift is active.' : 'Thank you for your generosity.'}</h4>
                <p>
                  {isMonthlyReturn
                    ? `Stripe confirmed your monthly gift of ${formatUsdCents(returnedAmountCents)}.`
                    : `Stripe confirmed your donation of ${formatUsdCents(returnedAmountCents)}.`}{' '}
                  {returnedEmail ? `A receipt will be sent to ${returnedEmail}.` : null}
                </p>
              </div>
            ) : null}

            {status === 'processing' ? (
              <div className="give-status-card give-status-card--processing">
                <p className="info-kicker">Payment processing</p>
                <h4>Your donation is still being confirmed.</h4>
                <p>
                  Stripe is processing {formatUsdCents(returnedAmountCents)} right now. Once it clears, the receipt will
                  go to {returnedEmail || 'your email address'}.
                </p>
              </div>
            ) : null}

            <div className="give-form-section">
              <label className="field-label">Donation frequency</label>
              <div className="give-frequency-toggle">
                <button
                  type="button"
                  onClick={() => updateFrequency('one-time')}
                  className={`give-frequency-button ${frequency === 'one-time' ? 'give-frequency-button--active' : ''}`}
                >
                  One-time gift
                </button>
                <button
                  type="button"
                  onClick={() => updateFrequency('monthly')}
                  className={`give-frequency-button ${frequency === 'monthly' ? 'give-frequency-button--active' : ''}`}
                >
                  Monthly support
                </button>
              </div>
            </div>

            {frequency === 'monthly' && monthlyNeedsAuth ? (
              <div className="give-auth-gate">
                <p className="info-kicker">Monthly giving</p>
                <h4>A donor account is required for recurring gifts.</h4>
                <p>Sign in or create a donor account so monthly giving can stay attached to your Victoria&apos;s Place portal access.</p>
                <div className="give-inline-actions">
                  <Link to="/sign-in?redirect=/give" className="button-secondary tap-target">
                    Sign in
                  </Link>
                  <Link to="/sign-in?redirect=/give&role=donor" className="button-primary tap-target">
                    Create donor account
                  </Link>
                </div>
              </div>
            ) : null}

            <div className="give-form-section">
              <label className="field-label">Choose an amount</label>
              <div className="give-amount-grid">
                {donationAmounts.map((item) => (
                  <button
                    key={item.amount}
                    type="button"
                    onClick={() => {
                      resetCheckoutState();
                      setSelectedAmount(item.amount);
                      setCustomAmount('');
                    }}
                    className={`give-amount-button ${selectedAmount === item.amount ? 'give-amount-button--active' : ''}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="give-form-section">
              <label htmlFor="custom-amount" className="field-label">
                Or enter a custom amount
              </label>
              <input
                id="custom-amount"
                type="number"
                min="1"
                step="0.01"
                placeholder="Custom amount"
                value={customAmount}
                onChange={(event) => {
                  resetCheckoutState();
                  setCustomAmount(event.target.value);
                  setSelectedAmount(null);
                }}
                className="field-input tap-target"
              />
            </div>

            <label className="give-fee-toggle">
              <input
                type="checkbox"
                checked={coverProcessingFee}
                onChange={(event) => {
                  resetCheckoutState();
                  setCoverProcessingFee(event.target.checked);
                }}
              />
              <span>
                Help cover processing costs
                {baseAmountCents > 0 ? (
                  <small>
                    This adds {formatUsdCents(calculateCoveredFeeCents(baseAmountCents))} so Victoria&apos;s Place can
                    receive the full intended gift.
                  </small>
                ) : null}
              </span>
            </label>

            <div className="give-donor-grid">
              <label className="auth-field">
                <span>Full name</span>
                <input
                  className="auth-input"
                  type="text"
                  placeholder="Your name"
                  value={donorName}
                  onChange={(event) => {
                    resetCheckoutState();
                    setDonorName(event.target.value);
                  }}
                />
              </label>

              <label className="auth-field">
                <span>Email for receipt</span>
                <input
                  className="auth-input"
                  type="email"
                  placeholder="you@example.com"
                  value={donorEmail}
                  onChange={(event) => {
                    resetCheckoutState();
                    setDonorEmail(event.target.value);
                  }}
                  required
                />
              </label>
            </div>

            {baseAmountCents > 0 ? (
              <div className="give-summary-card">
                <div className="give-summary-row">
                  <span>{frequency === 'monthly' ? 'Monthly gift' : 'Gift amount'}</span>
                  <strong>{formatUsdCents(baseAmountCents)}</strong>
                </div>
                {coverProcessingFee ? (
                  <div className="give-summary-row">
                    <span>Processing fee coverage</span>
                    <strong>{formatUsdCents(coveredFeeCents)}</strong>
                  </div>
                ) : null}
                <div className="give-summary-row">
                  <span>Estimated Stripe processing</span>
                  <strong>{formatUsdCents(estimatedProcessingFeeCents)}</strong>
                </div>
                <div className="give-summary-row give-summary-row--total">
                  <span>{frequency === 'monthly' ? 'Total monthly charge' : 'Total charge'}</span>
                  <strong>{formatUsdCents(chargedAmountCents)}</strong>
                </div>
              </div>
            ) : null}

            {checkoutError ? <div className="auth-error">{checkoutError}</div> : null}

            <button
              type="button"
              onClick={() => void handlePrepareCheckout()}
              className="button-primary tap-target give-submit-button"
              disabled={!parsedAmount || isPreparingCheckout || (frequency === 'monthly' && isInitializing)}
            >
              {isPreparingCheckout
                ? 'Preparing secure checkout...'
                : frequency === 'monthly'
                  ? `Start monthly giving (${formatUsdCents(chargedAmountCents)}/month)`
                  : `Continue to secure payment (${formatUsdCents(chargedAmountCents)})`}
            </button>

            {stripePromise && elementsOptions ? (
              <div className="give-payment-shell">
                <div className="give-payment-heading">
                  <p className="info-kicker">Secure checkout</p>
                  <h4>Complete your gift with Stripe.</h4>
                </div>

                <Elements stripe={stripePromise} options={elementsOptions}>
                  <DonationPaymentForm
                    chargedAmountCents={chargedAmountCents}
                    clientSecret={String(elementsOptions.clientSecret || '')}
                    donorEmail={donorEmail.trim()}
                    donorName={donorName.trim()}
                    frequency={frequency}
                    submitLabel={
                      frequency === 'monthly'
                        ? `Confirm ${formatUsdCents(chargedAmountCents)} monthly gift`
                        : `Pay ${formatUsdCents(chargedAmountCents)}`
                    }
                    onComplete={handlePaymentComplete}
                  />
                </Elements>
              </div>
            ) : null}

            <p className="give-form-note">
              Secure donations are processed through Stripe. Victoria&apos;s Place can receive funds through the connected
              account configured in the portal account settings.
            </p>
          </div>
        </motion.section>

        <section className="give-support-grid">
          {supportPoints.map((item, index) => (
            <motion.article
              key={item.title}
              className="give-support-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.42, delay: index * 0.05, ease: 'easeOut' }}
            >
              <p className="home-section-label">{item.title}</p>
              <p>{item.description}</p>
            </motion.article>
          ))}
        </section>

        <section className="give-trust-section">
          <motion.figure
            className="give-trust-photo"
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <img src={supportImage} alt="A child supported through the mission of Victoria's Place" />
          </motion.figure>

          <motion.div
            className="give-trust-copy"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.24 }}
            transition={{ duration: 0.48, ease: 'easeOut' }}
          >
            <p className="home-section-label">Stewardship</p>
            <h2>Your support helps sustain care that is personal, visible, and rooted in community.</h2>
            <div className="give-trust-list">
              {stewardshipNotes.map((note) => (
                <div key={note} className="give-trust-item">
                  <span />
                  <p>{note}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        <motion.section
          className="give-final-band"
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.24 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="give-final-copy">
            <p className="home-section-label home-section-label--light">Stay Involved</p>
            <h2>If you have questions about giving, volunteering, or partnership, we would love to hear from you.</h2>
          </div>

          <div className="give-final-actions">
            <Link to="/contact" className="button-secondary tap-target">
              Contact Victoria&apos;s Place
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
